import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync, useToast } from '../../lib/store';
import type { BreedingConfigResponse, BreedingEconomy } from '../../lib/types';
import { ErrorState, Panel } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * The admin breeding console — design PART 45 and 46.
 *
 * Two things it deliberately does *not* do:
 *
 *   - It never shows a stored RNG seed, because there is none. Every outcome is
 *     decided and written when an egg is created, so there is nothing an admin
 *     could read that would let them predict a future roll — the system has no
 *     secret to leak.
 *   - It never lets a value be set outside its bounds. The server clamps on
 *     read, and this panel echoes back the *effective* value rather than what
 *     was typed, so an admin who enters 10 into a field with a floor of 512
 *     sees 512 instead of believing they set 10.
 */
export function AdminBreedingTab() {
  return (
    <div className="stack">
      <EconomyPanel />
      <ConfigPanel />
    </div>
  );
}

function EconomyPanel() {
  const state = useAsync<BreedingEconomy>(() => api.get('/admin/breeding/economy'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 140 }} />;

  const data = state.data;
  // The number that actually matters. Everything else is context for it.
  const observed = data.shinyRate > 0 ? Math.round(1 / data.shinyRate) : 0;

  return (
    <Panel title="▤ Breeding economy" hint={`last ${data.windowDays} days`}>
      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}
      >
        <Metric label="Eggs bred" value={formatNumber(data.eggsBred)} />
        <Metric label="Eggs hatched" value={formatNumber(data.eggsHatched)} />
        <Metric label="Shinies hatched" value={formatNumber(data.shiniesHatched)} />
        <Metric
          label="Observed shiny rate"
          value={observed > 0 ? `1 in ${formatNumber(observed)}` : '—'}
        />
        <Metric label="Marked hatched" value={formatNumber(data.markedHatched)} />
        <Metric label="Active pairs" value={formatNumber(data.activePairs)} />
        <Metric label="Active hunts" value={formatNumber(data.activeHunts)} />
        <Metric label="Bloodlines" value={formatNumber(data.bloodlines)} />
        <Metric label="Eggs queued" value={formatNumber(data.queuedEggs)} />
      </div>
      <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
        Compare the observed rate against the base rate below. They should diverge — chains,
        heritage and bad-luck protection all push the observed rate lower than the base. A rate far
        <em> below</em> the base is the flooding signal.
      </p>
    </Panel>
  );
}

function ConfigPanel() {
  const { push } = useToast();
  const state = useAsync<BreedingConfigResponse>(() => api.get('/admin/breeding/config'));
  const [saving, setSaving] = useState<string | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 300 }} />;

  const groups = [...new Set(state.data.fields.map((field) => field.group))];

  async function save(key: string, value: string) {
    setSaving(key);
    try {
      const result = await api.patch<{ key: string; value: number }>('/admin/breeding/config', {
        key,
        value: Number(value),
      });
      push('success', `${key} = ${result.value}`);
      // Reload rather than trusting the local edit: the server may have clamped.
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save that.');
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      {groups.map((group) => (
        <Panel key={group} title={`⚙ ${group}`}>
          <div className="stack-sm">
            {state.data!.fields
              .filter((field) => field.group === group)
              .map((field) => (
                <div key={field.key} className="row row--between" style={{ gap: 'var(--gap-sm)' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong className="tiny">{field.label}</strong>
                    <div className="tiny muted">{field.help}</div>
                    {field.value !== field.default && (
                      <div className="tiny muted">default {field.default}</div>
                    )}
                  </span>
                  <input
                    // Keyed on the effective value so a save that was clamped
                    // remounts the field showing what actually took effect.
                    // Without this the box keeps displaying the rejected input
                    // and an admin walks away believing they set a shiny rate
                    // of 1-in-10 when the server floored it at 512.
                    key={`${field.key}:${field.value}`}
                    className="input"
                    type="number"
                    style={{ width: 110 }}
                    defaultValue={field.value}
                    disabled={saving === field.key}
                    onBlur={(event) => {
                      if (Number(event.target.value) !== field.value) {
                        void save(field.key, event.target.value);
                      }
                    }}
                  />
                </div>
              ))}
          </div>
        </Panel>
      ))}

      <p className="tiny muted">
        Values are clamped to a safe range on save, and the field shows what actually took effect.
        After changing anything under Shiny, re-run <code>npm run sim:breeding -w server</code>.
      </p>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="stack-sm" style={{ gap: 0 }}>
      <div className="tiny muted">{label}</div>
      <div className="mono">{value}</div>
    </div>
  );
}
